// Regras de valor default da tela de Parâmetros 

export const INDEXADORES_INDICE_PRECO_TR_PADRAO = [
    "IPCA",
    "IGPM",
    "IGPDI",
    "INPC",
    "INCC",
    "INCC_DI",
    "IPC_FIPE",
    "TR",
]

export const INDEXADOR_PRE = "PRE"
export const INDEXADOR_TR = "TR"
export const ID_JUROS_INDICE_PRECO = 10 // (1 + Taxa%)^(dup/dut)

export const FREQUENCIA_AMORTIZACAO_BULLET = 0

export const FREQUENCIA_RECOMPRA_NAO_TEM = 0
export const FREQUENCIA_RECOMPRA_INDETERMINADO = -1
export const MESES_RECOMPRA_INDETERMINADO = 12

export const TIPO_ATIVO_CRA = "CRA"
export const TIPOS_ATIVO_DEB = ["DEB", "DEB_INC"]
export const TIPOS_EMISSAO_IF = ["IF", "IF_IQ"]

export const CAMPO_TIPO_EMISSAO = "TipoEmissao"

export const CORRIGE_PROXIMO_DIA_UTIL = "Próximo Dia Útil"
export const CORRIGE_NAO_CORRIGE = "Não Corrige"
export const TIPO_CORRECAO_PADRAO = "Padrão"
export const TIPO_CORRECAO_VENCIMENTO = "Vencimento"
export const DIA_ANIVERSARIO_DEB = 15

export const normalizarCodigo = (valor) => String(valor ?? "").trim().toUpperCase()

const toNumberOrNull = (valor) => {
    if (valor === null || valor === undefined || `${valor}`.trim() === "") return null
    const numero = Number(valor)
    return Number.isNaN(numero) ? null : numero
}

const parseData = (valor) => {
    if (!valor) return null
    const texto = String(valor).trim()

    const br = texto.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
    if (br) return {dia: Number(br[1]), mes: Number(br[2]), ano: Number(br[3])}

    const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/)
    if (iso) return {dia: Number(iso[3]), mes: Number(iso[2]), ano: Number(iso[1])}

    return null
}

const pad = (numero) => String(numero).padStart(2, "0")

export const diaDaData = (valor) => parseData(valor)?.dia ?? null

// Soma meses mantendo o dia. Quando o dia não existe no mês de destino
// (ex.: 31/01 + 1 mês), usa o último dia do mês (28 ou 29/02).
export const somarMeses = (valor, meses) => {
    const data = parseData(valor)
    if (!data) return null

    const totalMeses = data.ano * 12 + (data.mes - 1) + meses
    const ano = Math.floor(totalMeses / 12)
    const mes = (totalMeses % 12) + 1
    const ultimoDiaDoMes = new Date(ano, mes, 0).getDate()
    const dia = Math.min(data.dia, ultimoDiaDoMes)

    return `${pad(dia)}/${pad(mes)}/${ano}`
}

export const obterTipoEmissao = (opcoesGrupoAtivos, tipoAtivo) => {
    const alvo = normalizarCodigo(tipoAtivo)
    if (!alvo) return null

    const opcoes = Array.isArray(opcoesGrupoAtivos) ? opcoesGrupoAtivos : []
    const opcao = opcoes.find(
        (item) => normalizarCodigo(item?.value ?? item?.original?.TipoAtivo) === alvo
    )

    return opcao?.original?.[CAMPO_TIPO_EMISSAO] ?? null
}

export const criarContextoDefaults = ({
                                          indexadoresIndicePrecoTR,
                                          jurosOptions = [],
                                          getTipoEmissao = () => null,
                                      } = {}) => {
    const carregados = Array.from(indexadoresIndicePrecoTR ?? []).map(normalizarCodigo).filter(Boolean)
    const lista = carregados.length > 0 ? carregados : INDEXADORES_INDICE_PRECO_TR_PADRAO

    return {
        indicesPrecoTR: new Set(lista),
        jurosOptions: Array.isArray(jurosOptions) ? jurosOptions : [],
        getTipoEmissao,
    }
}

const classificar = (formData, contexto) => {
    const caracteristicas = formData?.caracteristicas ?? {}
    const datas = formData?.datas ?? {}

    const indexador = normalizarCodigo(caracteristicas.indexador)
    const tipoAtivo = normalizarCodigo(caracteristicas.tipoAtivo)
    const tipoEmissao = normalizarCodigo(contexto.getTipoEmissao(caracteristicas.tipoAtivo))

    const isTR = indexador === INDEXADOR_TR
    const isIndicePrecoTR = isTR || contexto.indicesPrecoTR.has(indexador)

    return {
        isPre: indexador === INDEXADOR_PRE,
        isTR,
        isIndicePrecoTR,
        isIndicePreco: isIndicePrecoTR && !isTR,
        isCRA: tipoAtivo === TIPO_ATIVO_CRA,
        isDEB: TIPOS_ATIVO_DEB.includes(tipoAtivo),
        isIF: TIPOS_EMISSAO_IF.includes(tipoEmissao),
        diaEmissao: diaDaData(datas.dataEmissao),
        dataIntegralizacao: datas.dataIntegralizacao,
        frequenciaAmortizacao: toNumberOrNull(datas.frequenciaAmortizacao),
        frequenciaRecompra: toNumberOrNull(datas.frequenciaRecompra),
    }
}

export const REGRAS_DEFAULT = [
    {
        section: "datas",
        key: "dataCarenciaRecompra",
        valor: (c) => {
            const frequencia = c.frequenciaRecompra
            if (frequencia === null || frequencia === FREQUENCIA_RECOMPRA_NAO_TEM) return null

            const meses = frequencia === FREQUENCIA_RECOMPRA_INDETERMINADO
                ? MESES_RECOMPRA_INDETERMINADO
                : frequencia

            if (!(meses > 0)) return null
            return somarMeses(c.dataIntegralizacao, meses)
        },
    },
    {
        section: "jurosCorrecao",
        key: "defasagem",
        valor: (c) => {
            if (c.isPre) return 0
            if (c.isTR) return c.diaEmissao
            return 1
        },
    },
    {
        section: "jurosCorrecao",
        key: "formulaIndexador",
        valor: (c) => (c.isIndicePrecoTR ? ID_JUROS_INDICE_PRECO : null),
    },
    {
        section: "jurosCorrecao",
        key: "periodicidadeAtualizacaoMonetaria",
        valor: (c) => (c.isIndicePrecoTR ? 1 : 6),
    },
    {
        section: "jurosCorrecao",
        key: "projecaoInflacao",
        valor: (c) => {
            if (!c.isIndicePreco) return null
            return c.isCRA ? 1 : 0
        },
    },
    {
        section: "jurosCorrecao",
        key: "inflacaoNegativa",
        valor: (c) => (c.isIndicePreco ? 1 : null),
    },
    {
        section: "jurosCorrecao",
        key: "recalculoProjecao",
        valor: (c) => (c.isIndicePreco ? 1 : null),
    },
    {
        section: "jurosCorrecao",
        key: "aplicacaoAmortizacao",
        valor: (c) => (c.frequenciaAmortizacao === FREQUENCIA_AMORTIZACAO_BULLET ? "VNI" : "VNA"),
    },
    {
        section: "jurosCorrecao",
        key: "corrigeDataAniversario",
        valor: (c) => {
            if (!c.isIndicePrecoTR) return null
            return c.isIF || c.isDEB ? CORRIGE_PROXIMO_DIA_UTIL : CORRIGE_NAO_CORRIGE
        },
    },
    {
        section: "jurosCorrecao",
        key: "diaAniversario",
        valor: (c) => {
            if (!c.isIndicePrecoTR) return null
            if (c.isIF || c.isTR) return c.diaEmissao
            if (c.isDEB) return DIA_ANIVERSARIO_DEB
            return null
        },
    },
    {
        section: "jurosCorrecao",
        key: "tipoCorrecao",
        valor: (c) => {
            if (!c.isIndicePrecoTR) return null
            return c.isIF || c.isDEB || c.isTR ? TIPO_CORRECAO_PADRAO : TIPO_CORRECAO_VENCIMENTO
        },
    },
]

export const calcularDefaults = (formData, contexto) => {
    const c = classificar(formData, contexto)

    return REGRAS_DEFAULT.reduce((acc, {section, key, valor}) => {
        acc[section] = acc[section] ?? {}
        acc[section][key] = valor(c) ?? null
        return acc
    }, {})
}

const buscarCalculoDias = (jurosOptions, idJuros) => {
    if (idJuros === null || idJuros === undefined) return null
    const selecionado = jurosOptions.find((item) => Number(item.IDJuros) === Number(idJuros))
    return selecionado?.CalculoDias ?? null
}

const definirCampo = (state, section, key, valor, contexto) => {
    const secao = {...state[section], [key]: valor}

    if (section === "jurosCorrecao" && key === "formulaIndexador") {
        secao.calculoDiasIndexador = buscarCalculoDias(contexto.jurosOptions, valor)
    }

    return {...state, [section]: secao}
}

// Usado na edição de um campo: só reaplica o default dos campos cujo
// default mudou entre o estado anterior e o novo. Um valor digitado à mão
// é mantido até que algum campo do qual ele depende seja alterado.
export const aplicarDefaultsPorDiferenca = (prev, next, contexto) => {
    const antes = calcularDefaults(prev, contexto)
    const depois = calcularDefaults(next, contexto)

    return REGRAS_DEFAULT.reduce((acc, {section, key}) => {
        if (Object.is(antes[section][key], depois[section][key])) return acc
        return definirCampo(acc, section, key, depois[section][key], contexto)
    }, next)
}

// Usado quando a tela é montada a partir da Característica (sem parâmetros salvos).
export const aplicarTodosDefaults = (state, contexto, ignorar = new Set()) => {
    const defaults = calcularDefaults(state, contexto)

    return REGRAS_DEFAULT.reduce((acc, {section, key}) => {
        if (ignorar.has(`${section}.${key}`)) return acc
        return definirCampo(acc, section, key, defaults[section][key], contexto)
    }, state)
}